import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, unref as _unref, vShow as _vShow, isRef as _isRef, createVNode as _createVNode, withDirectives as _withDirectives, createElementVNode as _createElementVNode, withModifiers as _withModifiers, createElementBlock as _createElementBlock, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "studentInformationForm" }
const _hoisted_2 = ["disabled"]
const _hoisted_3 = { class: "xform-sections" }
const _hoisted_4 = { class: "xform-section" }
const _hoisted_5 = { class: "xfields" }
const _hoisted_6 = { class: "xform-section" }
const _hoisted_7 = { class: "row" }
const _hoisted_8 = {
  key: 0,
  class: "xform-buttons-wrapper d-flex justify-content-end"
}

import type { FormChange, FormSchema, FormPatch, KeyRename, CompareOptions } from "@/models/form";
import { FormPatchOperations } from "@/models/form";
import { computed, onMounted, watch, ref } from "vue";
import { $api } from "@/services/api";
import { Field as VeeField } from "vee-validate";
import BaseFormField from "@/components/shared/BaseFormField.vue";
import ParentAttestationConsent from "@/components/consents/ParentAttestationConsent.vue";
import { ConsentServiceEntityTypeIds } from "@/config/constants";
import { FormModes } from "@/models/form";
import NationalIdNumberInput from "@/components/inputs/NationalIdNumberInput.vue";
import { useStudentInformationForm } from "@/composables/forms/useStudentInformationForm";
import useApplicationData from "@/composables/useApplicationData";
import AddressInput from "@/components/v2/application/forms/address/AddressInput.vue";
import type { Address } from "@/models/application";
import { useStore } from "vuex";
import functions from "@/use/functions";
import type { StudentVM, StudentFormVM, StudentDto } from "@/models/student";
import modalModuleActions from "@/use/modalModuleActions";
import type { RootStore } from "@/models/root-store";
import NextButton from "@/components/shared/NextButton.vue";
import StudentAddressInput from "@/components/application-info/parent-loc/student/StudentAddressInput.vue";
import CircleLoader from "@/components/shared/CircleLoader.vue";
import { ButtonTypes, ModalTypes } from "@/models/modal";

interface Props {
  disabled: boolean;
  loading: boolean;
  student: StudentVM | null;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'StudentInformationForm',
  props: {
    disabled: { type: Boolean, default: true },
    loading: { type: Boolean },
    student: {}
  },
  emits: ["disableForm", "studentUpdated"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const addressInputRef = ref<InstanceType<typeof AddressInput>>();
const nationalIdNumberInputRef = ref<InstanceType<typeof NationalIdNumberInput>>();

const store = useStore();

const emit = __emit;

const props = __props;

let person = ref({
  applicantId: 0,
  firstName: "",
  lastName: "",
  dateOfBirth: "",
  citizenshipStatusId: 0,
  citizenshipStatusName: "",
  nationalIdNumberTypeId: 0,
  nationalIdNumber: "",
  parentConsent: undefined as undefined | boolean,
  parentAttestation: {},
});

const savingChanges = ref<boolean>(false);

const skipKeys = ref<string[]>([
  "addressId",
  "addressTypeId",
  "addressUnitTypeId",
  "citizenshipStatusId",
  "applicantId",
  "countryId",
  "fullName",
  "id",
  "initials",
  "parentAttestation",
  "parentConsent",
  "stateId",
  "consentGiven",
]);

const studentInformationModal = computed(
  () => store.state.applicationInfoModule.studentInformationModal,
);

const studentBackup = ref<StudentFormVM | undefined>(undefined);

const confirmChangesTitle = ref<string>("Save Student Information Changes");

const mode = computed(() => (props.disabled ? FormModes.view : FormModes.edit));

const originalStudent = computed<StudentDto>(() => {
  let student = {
    applicantId: selectedStudent.value?.applicantId || 0,
    applicationId: applicationId.value || 0,
    firstName: selectedStudent.value?.firstName?.trim() || "",
    lastName: selectedStudent.value?.lastName?.trim() || "",
    suffixTypeId: null,
    suffixTypeName: null,
    dateOfBirth: selectedStudent.value?.dateOfBirth + "T00:00:00",
    citizenshipStatusId: selectedStudent.value?.citizenshipStatusId || 0,
    citizenshipStatusName: selectedStudent.value?.citizenshipStatusName || "",
    nationalIdNumber: selectedStudent.value?.nationalIdNumber || "",
    nationalIdNumberTypeId: Number(selectedStudent.value?.nationalIdNumberTypeId),
    addresses: [] as Address[],
  };

  if (selectedStudent.value?.address) {
    student.addresses[0] = selectedStudent.value?.address;
  }

  return student;
});

const updatedStudent = computed<StudentDto>(() => {
  return {
    applicantId: person.value?.applicantId,
    applicationId: applicationId.value,
    firstName: person.value?.firstName?.trim(),
    lastName: person.value?.lastName?.trim(),
    suffixTypeId: null,
    suffixTypeName: null,
    dateOfBirth: person.value?.dateOfBirth + "T00:00:00",
    citizenshipStatusId: Number(person.value?.citizenshipStatusId),
    citizenshipStatusName: person.value?.citizenshipStatusName,
    nationalIdNumber: person.value?.nationalIdNumber,
    nationalIdNumberTypeId: Number(selectedStudent.value?.nationalIdNumberTypeId),
    addresses: [updatedAddress.value],
  };
});

const updatedAddress = computed<Address>(() => {
  return addressInputRef.value?.updatedAddress || ({} as Address);
});

const selectedStudent = computed<StudentVM | undefined>(
  () => store.state.loanConfigurationModule.applicationInfo.student,
);

const studentExists = computed<boolean>(
  () => store.state.loanConfigurationModule.applicationInfo?.hasStudent === true,
);

const {
  citizenshipStatusOptions,
  errors,
  formatDateOfBirth,
  handleFieldChange,
  isNationalIdNumberMasked,
  nationalIdNumberLabel,
  nationalIdNumberValidationSchema,
  schema,
  showPermanentResidentReminder,
  setValues,
  setFieldValue,
  values,
} = useStudentInformationForm({
  studentInfo: person,
  mode,
  initialValues: studentBackup,
  entityTypeId: ConsentServiceEntityTypeIds.Application,
});

async function updateStudentInformation(operations: FormPatch[]) {
  // filter out nationalIdnumber if it's the masked value
  // Follows XXX-XX-#### format
  const masked = isNationalIdNumberMasked(person.value?.nationalIdNumber);
  if (masked) {
    operations = operations.filter(o => o.path !== "/student/nationalIdNumber");
  }
  // use the application patch
  const response = await $api.applications.updateStudent(referenceId.value, {
    applicationId: applicationId.value,
    updatedStudent: updatedStudent.value,
    patch: operations,
  });

  if (response !== null && response.applicantId && response?.applicantId > 0) {
    emit("studentUpdated", response);
    savingChanges.value = false;
  } else {
    savingChanges.value = false;

    modalModuleActions.openModal({
      title: "Error",
      description: "There was an issue saving the Student",
      allowClickOutsideToClose: true,
    });
  }
}
const addressSchema = computed<FormSchema>(() => addressInputRef.value?.addressSchema);

const keysToRename = computed<KeyRename[]>(() => {
  return [
    {
      key: "firstName",
      rename: schema.value.firstName.label || "First Name",
    } as KeyRename,
    {
      key: "lastName",
      rename: schema.value.lastName.label || "Last Name",
    } as KeyRename,
    {
      key: "nationalIdNumber",
      rename: nationalIdNumberLabel.value || "",
    } as KeyRename,
    {
      key: "dateOfBirth",
      rename: schema.value.dateOfBirth.label || "Date of Birth",
    } as KeyRename,
    {
      key: "dateOfBirthDisplay",
      rename: schema.value.dateOfBirth.label || "Date of Birth",
    } as KeyRename,
    {
      key: "citizenshipStatusName",
      rename: schema.value.citizenshipStatusId.label || "Citizenship Status",
    } as KeyRename,
    {
      key: "address1",
      rename: addressSchema.value?.address1?.label || "Permanent Street Address",
    } as KeyRename,
    {
      key: "address2",
      rename: addressSchema.value?.address2?.label || "Unit Number",
    } as KeyRename,
    {
      key: "city",
      rename: addressSchema.value?.city?.label || "City",
    } as KeyRename,
    {
      key: "stateName",
      rename: addressSchema.value?.stateId?.label || "State",
    } as KeyRename,
    {
      key: "postalCode",
      rename: addressSchema.value?.postalCode?.label || "Zip Code",
    } as KeyRename,
    {
      key: "countryName",
      rename: addressSchema.value?.countryId?.label || "Country",
    } as KeyRename,
    {
      key: "addressUnitTypeName",
      rename: addressSchema.value?.addressUnitTypeId?.label || "Unit Type",
    } as KeyRename,
  ];
});

function generatePatchPath(pathArray: string[]) {
  return "/" + pathArray.join("/");
}

function generatePathOp(before: any, after: any): keyof typeof FormPatchOperations {
  let op = FormPatchOperations.replace;

  if (before === null && after !== null) {
    return "add";
  } else if (before !== null && after === null) {
    return "remove";
  } else {
    return op;
  }
}

function checkStudentDtoChanges(object1: StudentDto, object2: StudentDto, options: CompareOptions) {
  options.pathPrefix = "/student";

  ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  // description: returns an html string of changes in a form by comparing the before object with the after object //
  ///////////////////////////////////////////////////////////////////////////////////////////////////////////////////
  let html = "";

  const patches = [] as FormPatch[];
  const changes = [] as FormChange[];

  functions.compareObjects(object1, object2).forEach((reference: any) => {
    let before = reference.reduce((o: any, key: any) => o[key], object2);
    let after = reference.reduce((o: any, key: any) => o[key], object1);

    const path = options?.pathPrefix
      ? options?.pathPrefix + generatePatchPath(reference)
      : generatePatchPath(reference);

    // get the last variable from the reference array
    let key = reference[reference.length - 1];

    // get the last variable from the reference array, and convert the camelCase to add spaces
    let name = reference[reference.length - 1]?.replace(/([a-z])([A-Z])/g, `$1 $2`);

    // if (options?.keysToSkip?.includes(key)) {
    //   return;
    // }

    if (typeof before === "string") {
      before = before?.trim();
    }
    if (typeof after === "string") {
      after = after?.trim();
    }
    let rename = options?.keysToRename?.find((k: KeyRename) => k.key == key)?.rename;

    if (rename) {
      name = rename;
    }

    if (before === undefined || before === null || before === "") {
      before = null;
    }

    if (after === undefined || after === null || after === "") {
      after = null;
    }

    if (before === after) {
      // do not add to table if the values are the same
      return;
    }
    if (key === "nationalIdNumber") {
      after = nationalIdNumberInputRef.value?.rawValue;
    }
    patches.push({
      op: generatePathOp(before, after),
      path: path,
      value: after,
    });

    if (before === null) {
      before = "N/A";
    }

    if (after === null) {
      after = "N/A";
    }

    if (key === "dateOfBirth") {
      before = formatDateOfBirth(before?.split("T")[0]);
      after = formatDateOfBirth(after?.split("T")[0]);
    }

    if (key === "nationalIdNumber") {
      after = nationalIdNumberInputRef.value?.formatNationalIdNumber(after);
    }
    if (!options?.keysToSkip?.includes(key)) {
      changes.push({
        key: key,
        name: name,
        label: name,
        before: before,
        after: after,
        comment: `${functions.capitalize(name)} : ${after}`,
      });

      html += `<tr><td class="xbefore">${name}</td><td class="xafter">${before}</td><td class="xafter">${after}</td></tr>`;
    }
  });

  const filteredChanges = changes.filter(
    change => !(options?.keysToExcludeFromSummaryComment || []).includes(change.key),
  );
  const lastIndex = filteredChanges.length - 1;

  const changeSummaries = filteredChanges
    .map((change, index) => {
      if (index === lastIndex) {
        return change.comment + ".";
      } else if (index === 0) {
        return change.comment + " | ";
      } else {
        return change.comment + " " + " | ";
      }
    })
    .join("");

  const fieldsChanged = filteredChanges
    .map((change, index) => {
      if (filteredChanges.length === 1) {
        return change.name;
      } else if (filteredChanges.length === 2) {
        return index === lastIndex ? change.name : `${change.name} and `;
      } else {
        if (index === lastIndex) {
          return `and ${change.name}`;
        } else {
          return change.name + ", ";
        }
      }
    })
    .join("");

  const errorMessage = `${fieldsChanged} change${
    filteredChanges.length > 1 ? "s" : ""
  } could not be saved.`;

  const comment = `${options?.commentPrefix} ${changeSummaries}`;

  return {
    patches,
    changes,
    html,
    hasChanges: patches.length > 0,
    errorMessage,
    comment,
  };
}
const { applicationId, programTypeId, productTypeId, referenceId } = useApplicationData();

function showNoChangesSnackbar() {
  // Get the snackbar DIV
  const x = document.getElementById("snackbar-warning") as HTMLElement;

  // Add the "show" class to DIV
  x.className = "show";

  // After 3 seconds, remove the show class from DIV
  setTimeout(function () {
    x.className = x.className.replace("show", "");
  }, 3000);
}

function confirmChanges() {
  const options = {
    programTypeId: programTypeId.value,
    productTypeId: productTypeId.value,
    keysToSkip: skipKeys.value,
    keysToPick: [],
    keysToRename: keysToRename.value,
    keysToFormat: [],
    fieldsToCompare: [],
  } as CompareOptions;

  const formChanges = checkStudentDtoChanges(updatedStudent.value, originalStudent.value, options);

  if (formChanges.hasChanges) {
    modalModuleActions.openModal({
      title: confirmChangesTitle.value,
      description: "You made the following changes, please review and save.",
      html: null,
      changes: formChanges.changes,
      textAlign: "left",
      buttonsPosition: "right",
      type: ModalTypes.FormChanges,
      allowClickOutsideToClose: true,
      buttons: [
        {
          title: "Save Changes",
          type: ButtonTypes.Confirm,
          isCancel: false,
          onClick: async () => {
            store.dispatch("modal/setButtonLoading", { index: 0, flag: true });
            await updateStudentInformation(formChanges.patches);
          },
        },
      ],
    });
  } else {
    modalModuleActions.closeModal();
    showNoChangesSnackbar();
    resetSchemas();
    savingChanges.value = false;
  }
}
const hasErrors = computed<boolean>(() => Object.keys(errors.value).length > 0);

const validAddressInput = computed<boolean>(
  () => (addressInputRef.value && !addressInputRef.value?.hasErrors) === true,
);

function submit() {
  savingChanges.value = true;

  if (!hasErrors.value && validAddressInput.value) {
    confirmChanges();
  } else {
    savingChanges.value = false;
  }
}

function setStudentInformation() {
  if (studentExists.value && selectedStudent.value) {
    studentBackup.value = JSON.parse(JSON.stringify(selectedStudent.value));

    person.value = {
      applicantId: selectedStudent.value?.applicantId || 0,
      firstName: selectedStudent.value?.firstName || "",
      lastName: selectedStudent.value?.lastName || "",
      dateOfBirth: selectedStudent.value?.dateOfBirth || "",
      citizenshipStatusId: selectedStudent.value?.citizenshipStatusId,
      citizenshipStatusName: selectedStudent.value?.citizenshipStatusName,
      nationalIdNumber: selectedStudent.value?.nationalIdNumber,
      nationalIdNumberTypeId: selectedStudent.value?.nationalIdNumberTypeId,
      parentAttestation: selectedStudent.value.parentAttestation,
      parentConsent: selectedStudent.value.parentAttestation?.consentGiven,
    };

    setValues(person.value);

    // Set adddress
    addressInputRef.value?.setAddress();
  }
}
function resetSchemas() {
  for (const property in schema.value) {
    schema.value[property].valid = true;
    schema.value[property].errors = [];
    schema.value[property].touched = false;
  }

  addressInputRef.value?.resetSchemas();
}

function discardChanges() {
  person.value = JSON.parse(JSON.stringify(studentBackup.value));
  studentBackup.value = JSON.parse(JSON.stringify(person.value));

  setStudentInformation();
  resetSchemas();

  addressInputRef.value?.discardChanges();
}

// watch for the modal to close
const confirmChangesModalActive = computed<boolean>(
  () =>
    (store.state as RootStore).modal.active &&
    (store.state as RootStore).modal.title === confirmChangesTitle.value,
);

watch(
  () => confirmChangesModalActive.value,
  (newVal, oldVal) => {
    if (oldVal === true && newVal === false) {
      savingChanges.value = false;
    }
  },
);

watch(
  () => updatedAddress.value,
  newVal => {
    setFieldValue("address", newVal);
  },
  {
    deep: true,
  },
);

onMounted(async () => {
  if (studentExists.value) {
    setStudentInformation();
  }
});

__expose({
  addressInputRef,
  discardChanges,
  nationalIdNumberInputRef,
  studentInformationModal,
  values,
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (__props.loading)
      ? (_openBlock(), _createBlock(CircleLoader, { key: 0 }))
      : _createCommentVNode("", true),
    _withDirectives(_createElementVNode("form", _hoisted_1, [
      _createElementVNode("fieldset", { disabled: __props.disabled }, [
        _withDirectives(_createVNode(_unref(VeeField), {
          name: "person",
          modelValue: _unref(person),
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => (_isRef(person) ? (person).value = $event : person = $event))
        }, null, 8, ["modelValue"]), [
          [_vShow, false]
        ]),
        _createElementVNode("div", _hoisted_3, [
          _createElementVNode("div", _hoisted_4, [
            _createElementVNode("div", _hoisted_5, [
              _createVNode(BaseFormField, {
                schema: _unref(schema).firstName,
                modelValue: _unref(person).firstName,
                "onUpdate:modelValue": [
                  _cache[1] || (_cache[1] = ($event: any) => ((_unref(person).firstName) = $event)),
                  _cache[2] || (_cache[2] = ($event: any) => (_unref(handleFieldChange)('firstName', $event)))
                ]
              }, null, 8, ["schema", "modelValue"]),
              _createVNode(BaseFormField, {
                schema: _unref(schema).lastName,
                modelValue: _unref(person).lastName,
                "onUpdate:modelValue": [
                  _cache[3] || (_cache[3] = ($event: any) => ((_unref(person).lastName) = $event)),
                  _cache[4] || (_cache[4] = ($event: any) => (_unref(handleFieldChange)('lastName', $event)))
                ]
              }, null, 8, ["schema", "modelValue"]),
              _createVNode(BaseFormField, {
                schema: _unref(schema).dateOfBirth,
                modelValue: _unref(person).dateOfBirth,
                "onUpdate:modelValue": [
                  _cache[5] || (_cache[5] = ($event: any) => ((_unref(person).dateOfBirth) = $event)),
                  _cache[6] || (_cache[6] = ($event: any) => (_unref(handleFieldChange)('dateOfBirth', $event)))
                ]
              }, null, 8, ["schema", "modelValue"]),
              _createVNode(BaseFormField, {
                schema: _unref(schema).citizenshipStatusId,
                modelValue: _unref(person).citizenshipStatusId,
                "onUpdate:modelValue": [
                  _cache[7] || (_cache[7] = ($event: any) => ((_unref(person).citizenshipStatusId) = $event)),
                  _cache[8] || (_cache[8] = ($event: any) => (_unref(handleFieldChange)('citizenshipStatusId', $event)))
                ],
                modelModifiers: { number: true },
                options: _unref(citizenshipStatusOptions)
              }, null, 8, ["schema", "modelValue", "options"]),
              _createVNode(NationalIdNumberInput, {
                ref_key: "nationalIdNumberInputRef",
                ref: nationalIdNumberInputRef,
                nationalIdNumberTypeId: _unref(person).nationalIdNumberTypeId,
                modelValue: _unref(person).nationalIdNumber,
                "onUpdate:modelValue": [
                  _cache[9] || (_cache[9] = ($event: any) => ((_unref(person).nationalIdNumber) = $event)),
                  _cache[10] || (_cache[10] = ($event: any) => (_unref(handleFieldChange)('nationalIdNumber', $event)))
                ],
                schema: _unref(schema).nationalIdNumber,
                errors: _unref(errors).nationalIdNumber ? [_unref(errors).nationalIdNumber || ''] : [],
                disabled: savingChanges.value,
                rules: _unref(nationalIdNumberValidationSchema),
                showItinIndicator: false,
                "onUpdate:itinIndicator": _cache[11] || (_cache[11] = ($event: any) => (_unref(handleFieldChange)('itinIndicator', $event)))
              }, null, 8, ["nationalIdNumberTypeId", "modelValue", "schema", "errors", "disabled", "rules"])
            ])
          ]),
          _createVNode(StudentAddressInput, {
            ref_key: "addressInputRef",
            ref: addressInputRef,
            disabled: __props.disabled,
            mode: mode.value
          }, null, 8, ["disabled", "mode"]),
          _createElementVNode("div", _hoisted_6, [
            _createVNode(ParentAttestationConsent, {
              ref: "parentAttestationConsentRef",
              modelValue: _unref(person).parentConsent,
              "onUpdate:modelValue": [
                _cache[12] || (_cache[12] = ($event: any) => ((_unref(person).parentConsent) = $event)),
                _cache[13] || (_cache[13] = ($event: any) => (_unref(person).parentConsent = $event))
              ],
              disabled: __props.disabled || savingChanges.value,
              entityId: _unref(applicationId),
              entityTypeId: _unref(ConsentServiceEntityTypeIds).Application,
              details: selectedStudent.value?.parentAttestation
            }, null, 8, ["modelValue", "disabled", "entityId", "entityTypeId", "details"])
          ])
        ])
      ], 8, _hoisted_2),
      _cache[15] || (_cache[15] = _createElementVNode("div", { id: "snackbar-warning" }, "No changes to review.", -1)),
      _withDirectives(_createElementVNode("div", _hoisted_7, [...(_cache[14] || (_cache[14] = [
        _createElementVNode("div", { class: "col-10" }, [
          _createElementVNode("p", { class: "reminder-text" }, " Please note, proof of a current I-551 will be required for final application approval. ")
        ], -1)
      ]))], 512), [
        [_vShow, _unref(showPermanentResidentReminder) === true]
      ]),
      (mode.value === _unref(FormModes).edit)
        ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
            _createVNode(NextButton, {
              class: "submit",
              loading: savingChanges.value && !confirmChangesModalActive.value,
              disabled: savingChanges.value || confirmChangesModalActive.value,
              text: "Review Changes",
              type: "submit",
              onClick: _withModifiers(submit, ["prevent"])
            }, null, 8, ["loading", "disabled"])
          ]))
        : _createCommentVNode("", true)
    ], 512), [
      [_vShow, !__props.loading]
    ])
  ], 64))
}
}

})